import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { HomeIcon, CogIcon, LogoutIcon, InformationCircleIcon, UserGroupIcon, QuestionMarkCircleIcon } from '@heroicons/react/outline';
import React, { useContext, useCallback, useState, useMemo } from 'react';
import { GlobalStateContext } from '../context/GlobalStateContext';
import { PatientDataContext } from '../context/PatientDataContext';
import { ModularPatientDataContext } from '../context/ModularPatientDataContext';
import { getModuleLabel } from '../constants/assessmentModules';
import { ProformaNavigationGuardContext } from '../context/ProformaNavigationGuardContext';
import { useAuth } from '../hooks/useAuth';
import TermsPrivacyModal from './TermsPrivacyModal';
import NeedHelpModal from './NeedHelpModal';

export default function TopNavBar({ activePage }) {
  const { userData } = useContext(GlobalStateContext);
  const { clearPatientData } = useContext(PatientDataContext);
  // ModularPatientDataProvider only wraps autism-specific routes, so this
  // context will be undefined on most pages. Safe-destructure and only call
  // the clear function during logout when it exists.
  const modularCtx = useContext(ModularPatientDataContext);
  const clearModularPatientData = modularCtx?.clearPatientData;
  const { guardedNavigate, isDirty } = useContext(ProformaNavigationGuardContext);
  // Auth is the single source of truth for the current user's identity/claims.
  // We pass these into the support email so it carries useful diagnostic context.
  const { user, claims } = useAuth();
  const location = useLocation();

  // When the clinician is on a patient-specific route (e.g. /patient/:id/...),
  // we include the patient UID and reference number in the support email so the
  // support team can immediately locate the record. On non-patient pages (Dashboard,
  // Analytics, Settings, etc.) these fields are omitted.
  const activePatientId = useMemo(() => {
    const match = location.pathname.match(/^\/patient\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const { patientDataModel, loadedPatientId: adhdLoadedPatientId } = useContext(PatientDataContext);

  // Adult Autism (and future modular pathways) load patient data through
  // ModularPatientDataContext — NOT PatientDataContext. The provider wraps
  // autism route elements, so TopNavBar (inside Layout) can read header +
  // loadedPatientId from modularCtx when on those routes. ADHD patients use
  // PatientDataContext instead. We check both and use whichever has fresh data
  // for the patient in the URL.
  const modularLoadedPatientId = modularCtx?.loadedPatientId;
  const modularHeader = modularCtx?.header;

  const isModularDataCurrent =
    activePatientId != null && modularLoadedPatientId === activePatientId;
  const isAdhdDataCurrent =
    activePatientId != null && adhdLoadedPatientId === activePatientId;

  const currentPatientDoc = isModularDataCurrent
    ? modularHeader
    : isAdhdDataCurrent
      ? patientDataModel
      : null;

  // Autism-specific URL segments (/adult-autism, /proforma/adult-asd/, autism
  // review docs) let us infer the pathway before modular data finishes loading.
  const isAutismPatientRoute = useMemo(() => {
    if (!activePatientId) return false;
    return /\/adult-autism(?:\/|$)|\/proforma\/adult-asd\/|review-document-(ci|ssi|obs|dd|pad)-autism/.test(
      location.pathname,
    );
  }, [activePatientId, location.pathname]);

  const isCypAdhdPatientRoute = useMemo(() => {
    if (!activePatientId) return false;
    return /\/cyp-adhd(?:\/|$)/.test(location.pathname);
  }, [activePatientId, location.pathname]);

  const defaultPathwayLabel = useMemo(() => {
    if (!activePatientId) return null;
    if (isAutismPatientRoute) return getModuleLabel('adultAutism');
    if (isCypAdhdPatientRoute) return getModuleLabel('cypAdhd');
    return getModuleLabel('adultAdhd');
  }, [activePatientId, isAutismPatientRoute, isCypAdhdPatientRoute]);

  const activeReferenceNumber = useMemo(() => {
    if (!currentPatientDoc) return null;
    return currentPatientDoc?.customFields?.referenceNumber || null;
  }, [currentPatientDoc]);

  // assessmentModule is 'adultAdhd', 'adultAutism' or 'cypAdhd' on the patient
  // doc. The label comes from the module registry so a new pathway does not
  // silently display as "Adult ADHD" — which would be actively misleading on a
  // CYP record, where the patient is a child. Falls back to the route-derived
  // label while the patient doc is still loading.
  const activeAssessmentPathway = useMemo(() => {
    if (!activePatientId) return null;
    if (!currentPatientDoc) return defaultPathwayLabel;
    return getModuleLabel(currentPatientDoc.assessmentModule);
  }, [activePatientId, currentPatientDoc, defaultPathwayLabel]);

  const userInitials = userData?.firstName && userData?.lastName
    ? `${userData.firstName[0]}${userData.lastName[0]}`
    : '';

  // Controls the Terms & Privacy pop-up. Kept as local state here since the
  // modal has a single trigger button and no external state dependencies.
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  // Controls the "Need help?" support pop-up (same single-trigger pattern).
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  
  const navigate = useNavigate();  
  
  const handleLogout = useCallback(async () => {
    const doLogout = async () => {
      try {
        await signOut(auth);
        clearPatientData();
        if (clearModularPatientData) clearModularPatientData();
        navigate('/login');
      } catch (error) {
        console.error('Error logging out:', error);
      }
    };

    if (isDirty) {
      guardedNavigate('/login', doLogout);
    } else {
      await doLogout();
    }
  }, [isDirty, guardedNavigate, clearPatientData, clearModularPatientData, navigate]);

  return (
    <nav className="flex items-center justify-between shadow-sm px-8 py-2 border-b border-gray-200 z-10">
      <div className="flex items-center gap-2 font-semibold">      
        <img src="/td_logo_full_2025.png" alt="Logo" className="mx-4 my-1" style={{ width: '125px', height: 'auto' }} />
        <button
          onClick={() => guardedNavigate('/dashboard')}
          className={`flex items-center p-2 rounded-md transition duration-400 text-sm text-gray-700 hover:bg-violet-200`}
        >
          <HomeIcon className="w-5 h-5" />
          <span className="ml-1 hide-top-navbar-text">Dashboard</span>
        </button>
        <button
          onClick={() => guardedNavigate('/analytics')}
          className={`flex items-center p-2 rounded-md transition duration-400 text-sm ${
            activePage === 'analytics' ? 'bg-primary/90 text-white' : 'text-gray-700 hover:bg-violet-200'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="ml-1 hide-top-navbar-text">Analytics</span>
        </button>
        <button
          onClick={() => guardedNavigate('/settings')}
          className={`flex items-center p-2 rounded-md transition duration-400 text-sm ${
            activePage === 'settings' ? 'bg-primary/90 text-white' : 'text-gray-700 hover:bg-violet-200'
          }`}
        >
          <CogIcon className="w-5 h-5" />
          <span className="ml-1 hide-top-navbar-text">Settings</span>
        </button>
        {/*
          Staff management navigation:
          - We deliberately expose the "Staff" area to Provider Org. Admins and Support users.
          - Clinicians and all Service User account types are routed away from this link because
            they should not be able to manage staff accounts.
        */}
        {userData?.accountType === 'Provider' &&
          (userData?.userType === 'Org. Admin' || userData?.userType === 'Support') && (
            <button
              onClick={() => guardedNavigate('/staff')}
              className={`flex items-center p-2 rounded-md transition duration-400 text-sm ${
                activePage === 'staff' ? 'bg-primary/90 text-white' : 'text-gray-700 hover:bg-violet-200'
              }`}
            >
              <UserGroupIcon className="w-5 h-5" />
              <span className="ml-1 hide-top-navbar-text">Staff</span>
            </button>
          )}
      </div>
      <div className="flex items-center gap-2 font-bold">
        {/* Need help? trigger — opens the shared NeedHelpModal */}
        <button
          onClick={() => setIsHelpOpen(true)}
          className="flex items-center p-2 text-gray-700 hover:bg-gray-200 rounded-md transition duration-400 text-sm"
        >
          <QuestionMarkCircleIcon className="w-5 h-5" />
          <span className="ml-1 hide-top-navbar-text">Get support</span>
        </button>
        <NeedHelpModal
          open={isHelpOpen}
          onOpenChange={setIsHelpOpen}
          userId={user?.uid}
          organisation={claims?.organisation}
          patientId={activePatientId}
          referenceNumber={activeReferenceNumber}
          assessmentPathway={activeAssessmentPathway}
        />
        {/* Terms & Privacy trigger — opens the shared TermsPrivacyModal */}
        <button
          onClick={() => setIsTermsOpen(true)}
          className="flex items-center p-2 text-gray-700 hover:bg-gray-200 rounded-md transition duration-400 text-sm"
        >
          <InformationCircleIcon className="w-5 h-5" />
          <span className="ml-1 hide-top-navbar-text">Terms &amp; Privacy</span>
        </button>
        <TermsPrivacyModal open={isTermsOpen} onOpenChange={setIsTermsOpen} />
        <button
          onClick={handleLogout}
          className="flex items-center p-2 text-gray-700 hover:bg-gray-200 rounded-md transition duration-400 text-sm"
        >
          <LogoutIcon className="w-5 h-5" />
          <span className="ml-1 hide-top-navbar-text">Log out</span>
        </button>
        <div className="bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold">
          {userInitials}
        </div>
      </div>
    </nav>
  );
}
